/**
 * NinjaTrader Template Parser
 *
 * Parses ATM Strategy and Stop Strategy XML files from NinjaTrader 8
 * into structured data for analytics and display.
 *
 * Uses browser's built-in DOMParser — no external dependencies.
 */

import { getExecutionInstantMs } from './timeService.js';

// ── Helper: safely get text content from an XML element ──
const getXmlText = (parent, tagName) => {
  const el = parent.querySelector(tagName);
  return el ? el.textContent.trim() : '';
};

const getXmlInt = (parent, tagName) => parseInt(getXmlText(parent, tagName), 10) || 0;

// ── Parse a StopStrategy element (either nested in ATM or standalone) ──
const parseStopStrategyElement = (stopStrategyEl) => {
  if (!stopStrategyEl) return null;

  const autoTrailSteps = [];
  const stepsContainer = stopStrategyEl.querySelector('AutoTrailSteps');
  if (stepsContainer) {
    const stepEls = stepsContainer.querySelectorAll('AutoTrailStep');
    stepEls.forEach(step => {
      autoTrailSteps.push({
        frequency: getXmlInt(step, 'Frequency'),
        profitTrigger: getXmlInt(step, 'ProfitTrigger'),
        stopLoss: getXmlInt(step, 'StopLoss')
      });
    });
  }

  return {
    name: getXmlText(stopStrategyEl, 'Template') || 'Unnamed',
    autoBreakEvenPlus: getXmlInt(stopStrategyEl, 'AutoBreakEvenPlus'),
    autoBreakEvenProfitTrigger: getXmlInt(stopStrategyEl, 'AutoBreakEvenProfitTrigger'),
    autoTrailSteps
  };
};

// ── Bracket type classification (v7.6.2 — Diaz Doctrine prep) ──────────────
// Each bracket in an ATM template is structurally one of three kinds:
//
//   'TP'      — fixed take-profit target (targetTicks > 0). The exit is the
//               profit target; any stop strategy reference is moot because
//               the target fires before the trail engages.
//   'AT'      — auto-trail with a stop strategy that has actual trail steps
//               (targetTicks === 0, stopStrategy.autoTrailSteps non-empty).
//               The exit is the dynamic trail.
//   'static'  — no fixed target AND no trail strategy. The bracket runs on
//               the initial stop loss indefinitely until manually closed
//               OR the static stop hits. This is the "1 Runner — let it
//               ride with manual management" pattern in Chris's TP
//               templates (Target 3 with Profit=0 + Stop Strategy=None).
//
// `exitType` (legacy field) only distinguished 'takeProfit' vs 'trailingStop'
// — it conflated the static-runner case with the active-trail case. The new
// `type` field on each bracket is what analyzers should read going forward.
export const classifyBracket = (bracket) => {
  if (!bracket) return 'static';
  if (bracket.targetTicks > 0) return 'TP';
  if (bracket.stopStrategy?.autoTrailSteps?.length > 0) return 'AT';
  return 'static';
};

// ── Template-level type classification ──
// Derived from the mix of bracket types. The template-level type is what
// downstream analyzers should reason about ("this is an AT template, so
// trail-frequency suggestions apply" / "this is a TP template, so MFE-past-
// target is opportunity cost not free overshoot"). A template with at least
// one AT-leg AND at least one TP-leg is 'Hybrid'; everything else inherits
// from the dominant leg type. A pure-static template (rare — would mean no
// targets AND no trails) classifies as 'TP' since the static stop is the
// only exit and it behaves more like a fixed-distance exit than a trail.
export const classifyTemplate = (brackets) => {
  if (!Array.isArray(brackets) || brackets.length === 0) return 'Unknown';
  let hasAT = false;
  let hasTP = false;
  for (const b of brackets) {
    const t = classifyBracket(b);
    if (t === 'AT') hasAT = true;
    if (t === 'TP') hasTP = true;
  }
  if (hasAT && hasTP) return 'Hybrid';
  if (hasAT) return 'AT';
  if (hasTP) return 'TP';
  // All-static (e.g., everything is Profit=0 + Stop Strategy=None) is rare
  // but maps closest to TP semantics — the static stop is a fixed exit, not
  // a dynamic trail.
  return 'TP';
};

/**
 * Parse an ATM Strategy XML string into structured data.
 *
 * @param {string} xmlString - Raw XML content from NinjaTrader ATM template file
 * @returns {object|null} Parsed ATM structure, or null if parsing fails
 *
 * Return shape (v7.6.2):
 *   {
 *     name, entryQuantity, calculationMode,
 *     templateType: 'AT' | 'TP' | 'Hybrid' | 'Unknown',
 *     brackets: [{ ..., type: 'AT' | 'TP' | 'static', exitType: legacy }]
 *   }
 */
export const parseAtmXml = (xmlString) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      console.warn('[NT Parser] XML parse error:', parseError.textContent);
      return null;
    }

    const atmStrategy = doc.querySelector('AtmStrategy');
    if (!atmStrategy) return null;

    const name = getXmlText(atmStrategy, 'Template');
    const entryQuantity = getXmlInt(atmStrategy, 'EntryQuantity');
    const calculationMode = getXmlText(atmStrategy, 'CalculationMode') || 'Ticks';

    const brackets = [];
    const bracketsContainer = atmStrategy.querySelector('Brackets');
    if (bracketsContainer) {
      const bracketEls = bracketsContainer.querySelectorAll(':scope > Bracket');
      // Fallback if :scope doesn't work in some environments
      const bracketList = bracketEls.length > 0 ? bracketEls : bracketsContainer.children;

      Array.from(bracketList).forEach((bracketEl, index) => {
        if (bracketEl.tagName !== 'Bracket') return;

        const quantity = getXmlInt(bracketEl, 'Quantity');
        const stopLossTicks = getXmlInt(bracketEl, 'StopLoss');
        const targetTicks = getXmlInt(bracketEl, 'Target');
        const exitType = targetTicks > 0 ? 'takeProfit' : 'trailingStop';

        let stopStrategy = null;
        if (exitType === 'trailingStop') {
          const ssEl = bracketEl.querySelector('StopStrategy');
          stopStrategy = parseStopStrategyElement(ssEl);
        }

        // Classify this bracket against the AT/TP/static taxonomy. Done HERE
        // (not inferred at every consumer) so the canonical type lives on
        // the parsed object and analyzers can read it directly.
        const bracket = {
          index,
          quantity,
          stopLossTicks,
          targetTicks,
          exitType,
          stopStrategy,
        };
        bracket.type = classifyBracket(bracket);
        brackets.push(bracket);
      });
    }

    return {
      name,
      entryQuantity,
      calculationMode,
      brackets,
      // Template-level type derived from the mix of bracket types.
      //
      // Plumbed (v7.6.2 / Task A.2) into 4 downstream analyzers:
      //   atmVersionComparison — flags TP↔AT switches as categorical, not parametric
      //   atmParamDirection    — typeBreakdown in cross-template aggregation
      //   atmTuningSuggestion  — AT_ONLY_FIELDS safety on Path B transfer candidates
      //   emaProximityAtm      — trail vs static-target detail/action shading
      //
      // Each analyzer null-safes the read (`v?.snapshot?.templateType`)
      // because stub snapshots exist (useAtmManagement.js:427) and parser
      // failures yield a null parse result that never reaches the snapshot.
      templateType: classifyTemplate(brackets),
    };
  } catch (e) {
    console.warn('[NT Parser] Failed to parse ATM XML:', e.message);
    return null;
  }
};

/**
 * Parse a standalone Stop Strategy XML string.
 *
 * @param {string} xmlString - Raw XML content from NinjaTrader StopStrategy file
 * @returns {object|null} Parsed stop strategy, or null if parsing fails
 */
export const parseStopStrategyXml = (xmlString) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');

    const parseError = doc.querySelector('parsererror');
    if (parseError) return null;

    const ssEl = doc.querySelector('StopStrategy');
    if (!ssEl) return null;

    return parseStopStrategyElement(ssEl);
  } catch (e) {
    console.warn('[NT Parser] Failed to parse StopStrategy XML:', e.message);
    return null;
  }
};

/**
 * Match a trade's exits to ATM brackets by quantity and chronological order.
 *
 * NinjaTrader fills brackets in order — first bracket exits first.
 * We match by quantity: each exit's qty should match a bracket's qty.
 *
 * @param {object} trade - Journal trade with exits[] array
 * @param {object} parsedAtm - Parsed ATM from parseAtmXml()
 * @param {object} spec - Instrument spec { tickSize, tickValue, pointValue }
 * @returns {Array|null} Matched bracket results, or null if matching fails
 */
export const matchExitsToBrackets = (trade, parsedAtm, spec) => {
  if (!trade || !parsedAtm || !spec) return null;
  if (!trade.exits || trade.exits.length === 0) return null;
  if (!parsedAtm.brackets || parsedAtm.brackets.length === 0) return null;

  const validExits = trade.exits.filter(e => e.quantity && e.price);
  if (validExits.length === 0) return null;

  // Strategy: match exits to brackets by quantity in order
  const results = [];
  const usedBrackets = new Set();
  const entryPrice = parseFloat(trade.entry);
  const isLong = trade.direction === 'long';

  // Total risk for R calculation
  const slTicks = parsedAtm.brackets[0]?.stopLossTicks || 0;
  if (slTicks === 0) return null;

  for (const exit of validExits) {
    const exitQty = parseInt(exit.quantity);
    const exitPrice = parseFloat(exit.price);
    if (!exitQty || !exitPrice) continue;

    // Find matching bracket by quantity (first unused match)
    let matchedBracket = null;
    for (const bracket of parsedAtm.brackets) {
      if (usedBrackets.has(bracket.index)) continue;
      if (bracket.quantity === exitQty) {
        matchedBracket = bracket;
        usedBrackets.add(bracket.index);
        break;
      }
    }

    if (!matchedBracket) {
      // Try matching remaining unmatched brackets in order
      for (const bracket of parsedAtm.brackets) {
        if (usedBrackets.has(bracket.index)) continue;
        matchedBracket = bracket;
        usedBrackets.add(bracket.index);
        break;
      }
    }

    if (!matchedBracket) continue;

    // Calculate R for this specific bracket exit
    const priceDiff = isLong ? (exitPrice - entryPrice) : (entryPrice - exitPrice);
    const exitTicks = priceDiff / spec.tickSize;
    const rCaptured = slTicks > 0 ? exitTicks / slTicks : 0;

    results.push({
      bracketIndex: matchedBracket.index,
      exitType: matchedBracket.exitType,
      quantity: exitQty,
      exitPrice,
      rCaptured: parseFloat(rCaptured.toFixed(2)),
      stopStrategy: matchedBracket.stopStrategy
    });
  }

  return results.length > 0 ? results : null;
};

/**
 * Find a parsed ATM template that matches a trade's ATM strategy name.
 * Uses exact match first, then fuzzy substring match.
 *
 * @param {string} atmName - Trade's ATM strategy name
 * @param {Array} parsedAtmList - Array of parsed ATM templates
 * @returns {object|null} Matching parsed ATM template
 */
export const findMatchingTemplate = (atmName, parsedAtmList) => {
  if (!atmName || !parsedAtmList || parsedAtmList.length === 0) return null;

  // Exact match
  const exact = parsedAtmList.find(t => t.name === atmName);
  if (exact) return exact;

  // Fuzzy: ATM name contains template name or vice versa
  const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const normName = normalize(atmName);
  return parsedAtmList.find(t => {
    const normTemplate = normalize(t.name);
    return normName.includes(normTemplate) || normTemplate.includes(normName);
  }) || null;
};

// ═══════════════════════════════════════════════════════════════
// ── ATM Template Versioning ──────────────────────────────────
// ═══════════════════════════════════════════════════════════════

/**
 * Compute a stable fingerprint for an ATM template.
 * Only includes parameters that affect trading behavior (excludes names).
 * Used to detect when a template's actual config has changed.
 *
 * @param {object} parsedAtm - Parsed ATM from parseAtmXml()
 * @returns {string|null} Canonical JSON string, or null if invalid
 */
export const computeAtmFingerprint = (parsedAtm) => {
  if (!parsedAtm || !parsedAtm.brackets) return null;

  const canonical = {
    entryQuantity: parsedAtm.entryQuantity,
    calculationMode: parsedAtm.calculationMode,
    brackets: parsedAtm.brackets.map(b => ({
      quantity: b.quantity,
      stopLossTicks: b.stopLossTicks,
      targetTicks: b.targetTicks,
      exitType: b.exitType,
      stopStrategy: b.stopStrategy ? {
        autoBreakEvenPlus: b.stopStrategy.autoBreakEvenPlus,
        autoBreakEvenProfitTrigger: b.stopStrategy.autoBreakEvenProfitTrigger,
        autoTrailSteps: (b.stopStrategy.autoTrailSteps || []).map(s => ({
          frequency: s.frequency,
          profitTrigger: s.profitTrigger,
          stopLoss: s.stopLoss
        }))
      } : null
    }))
  };

  return JSON.stringify(canonical);
};

/**
 * Diff two ATM template versions to produce a human-readable change list.
 *
 * @param {object} oldVersion - Version object with .snapshot (older)
 * @param {object} newVersion - Version object with .snapshot (newer)
 * @returns {Array} Array of { bracket?, field, old, new } change objects
 */
export const diffAtmVersions = (oldVersion, newVersion) => {
  if (!oldVersion?.snapshot || !newVersion?.snapshot) return [];

  const changes = [];
  const oldSnap = oldVersion.snapshot;
  const newSnap = newVersion.snapshot;

  // Top-level changes
  if (oldSnap.entryQuantity !== newSnap.entryQuantity) {
    changes.push({ field: 'entryQuantity', old: oldSnap.entryQuantity, new: newSnap.entryQuantity });
  }

  const maxBrackets = Math.max(
    (oldSnap.brackets || []).length,
    (newSnap.brackets || []).length
  );

  for (let i = 0; i < maxBrackets; i++) {
    const ob = (oldSnap.brackets || [])[i];
    const nb = (newSnap.brackets || [])[i];

    if (!ob && nb) {
      changes.push({ bracket: i, field: 'bracketAdded', new: nb });
      continue;
    }
    if (ob && !nb) {
      changes.push({ bracket: i, field: 'bracketRemoved', old: ob });
      continue;
    }
    if (!ob || !nb) continue;

    // Compare bracket-level fields
    for (const key of ['quantity', 'stopLossTicks', 'targetTicks']) {
      if (ob[key] !== nb[key]) {
        changes.push({ bracket: i, field: key, old: ob[key], new: nb[key] });
      }
    }

    if (ob.exitType !== nb.exitType) {
      changes.push({ bracket: i, field: 'exitType', old: ob.exitType, new: nb.exitType });
    }

    // Compare stop strategy if both brackets have trailing stops
    const oldSS = ob.stopStrategy;
    const newSS = nb.stopStrategy;

    if (oldSS && newSS) {
      for (const key of ['autoBreakEvenPlus', 'autoBreakEvenProfitTrigger']) {
        if (oldSS[key] !== newSS[key]) {
          changes.push({ bracket: i, field: key, old: oldSS[key], new: newSS[key] });
        }
      }

      // Compare auto trail steps
      const oldSteps = oldSS.autoTrailSteps || [];
      const newSteps = newSS.autoTrailSteps || [];
      const maxSteps = Math.max(oldSteps.length, newSteps.length);

      for (let s = 0; s < maxSteps; s++) {
        const os = oldSteps[s];
        const ns = newSteps[s];

        if (!os && ns) {
          changes.push({ bracket: i, field: 'trailStepAdded', step: s, new: ns });
          continue;
        }
        if (os && !ns) {
          changes.push({ bracket: i, field: 'trailStepRemoved', step: s, old: os });
          continue;
        }
        if (!os || !ns) continue;

        for (const key of ['frequency', 'profitTrigger', 'stopLoss']) {
          if (os[key] !== ns[key]) {
            changes.push({ bracket: i, field: `trail_${key}`, step: s, old: os[key], new: ns[key] });
          }
        }
      }
    } else if (!oldSS && newSS) {
      changes.push({ bracket: i, field: 'stopStrategyAdded', new: newSS });
    } else if (oldSS && !newSS) {
      changes.push({ bracket: i, field: 'stopStrategyRemoved', old: oldSS });
    }
  }

  return changes;
};

/**
 * Human-readable label for a diff field name.
 */
export const diffFieldLabel = (field) => {
  const labels = {
    entryQuantity: 'Qty',
    quantity: 'Qty',
    stopLossTicks: 'SL',
    targetTicks: 'TP',
    exitType: 'Exit Type',
    autoBreakEvenPlus: 'BE+',
    autoBreakEvenProfitTrigger: 'BE Trigger',
    trail_frequency: 'Trail Freq',
    trail_profitTrigger: 'Trail Trigger',
    trail_stopLoss: 'Trail SL',
  };
  return labels[field] || field;
};

/**
 * Find the ATM template version that was active when a trade was taken.
 * Versions are sorted newest-first. We find the latest version detected
 * before or at the trade's entry time.
 *
 * @param {object} trade - Journal trade with date ("YYYY-MM-DD") and entryTime ("HH:MM")
 * @param {Array} versions - Version array for this ATM, sorted newest-first
 * @returns {object|null} Matching version object, or null if no versions
 */
export const findVersionForTrade = (trade, versions) => {
  if (!versions || versions.length === 0) return null;
  if (versions.length === 1) return versions[0]; // Only one version — always matches

  // Build trade timestamp in **ET** via getExecutionInstantMs. Naïvely doing
  // `new Date("2026-05-14T21:46:00")` parses as browser-local time AND
  // anchors to the literal calendar date — both wrong for a futures journal
  // where the 21:46 fill on the "May 14 trading session" actually happened
  // on May 13 evening. getExecutionInstantMs is ET-anchored AND
  // session-boundary aware, matching how version.detectedAt (an ISO UTC
  // string) is stored. Without this fix, trades right around a version-
  // change boundary get attributed to the wrong version.
  let tradeMs = null;
  if (trade.date && trade.entryTime) {
    tradeMs = getExecutionInstantMs(trade.date, trade.entryTime);
  } else if (trade.date) {
    // Date-only: use noon ET via the same helper for stable ordering.
    tradeMs = getExecutionInstantMs(trade.date, '12:00');
  }

  if (!tradeMs || isNaN(tradeMs)) {
    return versions[versions.length - 1]; // Oldest version as fallback
  }

  // Find latest version detected before or at the trade entry. detectedAt
  // is an ISO UTC string written by `new Date().toISOString()`, so
  // Date.parse handles it correctly (UTC).
  for (const v of versions) {
    const detectedMs = new Date(v.detectedAt).getTime();
    if (detectedMs <= tradeMs) return v;
  }

  // Trade is older than all versions — use oldest version
  return versions[versions.length - 1];
};
